import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './ImageCropper.css';

/**
 * Composant de recadrage d'image carré
 * Utilisé pour les photos de profil
 */
function ImageCropper({ image, onCropComplete, onCancel, aspectRatio = 1, outputFormat = 'image/jpeg' }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropChange = useCallback((crop) => {
        setCrop(crop);
    }, []);

    const onZoomChange = useCallback((zoom) => {
        setZoom(zoom);
    }, []);

    const onCropCompleteInternal = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleConfirm = async () => {
        if (!croppedAreaPixels) return;

        try {
            const croppedImage = await getCroppedImg(image, croppedAreaPixels, 512, outputFormat);
            onCropComplete(croppedImage);
        } catch (error) {
            console.error('Error cropping image:', error);
        }
    };

    return (
        <div className="image-cropper-overlay">
            <div className="image-cropper-modal">
                <div className="image-cropper-header">
                    <h3>Recadrer la photo</h3>
                    <p>Ajustez le cadrage pour obtenir un format carré</p>
                </div>

                <div className="image-cropper-container">
                    <Cropper
                        image={image}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspectRatio}
                        onCropChange={onCropChange}
                        onZoomChange={onZoomChange}
                        onCropComplete={onCropCompleteInternal}
                        cropShape="rect"
                        showGrid={true}
                    />
                </div>

                <div className="image-cropper-controls">
                    <label>Zoom</label>
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                    />
                </div>

                <div className="image-cropper-actions">
                    <button className="btn-cancel" onClick={onCancel}>
                        Annuler
                    </button>
                    <button className="btn-confirm" onClick={handleConfirm}>
                        Valider
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Crée une image recadrée et redimensionnée à partir des coordonnées
 * Taille max: 512x512 pour optimiser les signatures email
 */
async function getCroppedImg(imageSrc, pixelCrop, maxSize = 512, outputFormat = 'image/jpeg') {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    // Calculer la taille finale (max 512x512)
    const outputSize = Math.min(pixelCrop.width, pixelCrop.height, maxSize);

    // Taille du canvas = taille de sortie
    canvas.width = outputSize;
    canvas.height = outputSize;

    // Activer le lissage pour un meilleur redimensionnement
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Dessiner l'image recadrée et redimensionnée
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        outputSize,
        outputSize
    );

    // Qualité selon le format
    const quality = outputFormat === 'image/png' ? undefined : 0.85;

    // Retourner comme Blob
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                resolve(blob);
            },
            outputFormat,
            quality
        );
    });
}

/**
 * Charge une image depuis une URL
 */
function createImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.src = url;
    });
}

export default ImageCropper;
